import Box from "@mui/material/Box";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useAuth } from "modules/auth/auth.context";
import {
  ControlledDatePicker,
  ControlledFileUpload,
  ControlledSelect,
  ControlledTextField,
  ModalWrapper,
  SectionTitle,
} from "modules/shared/components";
import { capitalizeString } from "modules/shared/utils";
import { toast } from "react-toastify";
import { logError } from "utils/logger";
import {
  ADMIN_LEAVE_APPLICATION_DEFAULT_VALUES,
  LEAVES_SESSION_OPTIONS,
  LEAVE_TYPE_OPTIONS,
} from "../constants";
import { useAdminLeaves } from "../hooks/useAdminLeaves";
import { applyLeaveForEmployee } from "../service";
import { leaveApplicationValidation } from "../utils/leaveApplication.validation";

/**
 * Admin Leave Application Modal
 * @param {Object} props
 * @param {Function} props.onRefresh - Callback function to refresh the leave application list
 * @returns {JSX.Element}
 */
export default function AdminLeaveApplicationModal({ onRefresh }) {
  const { user } = useAuth();
  const adminId = user?.employee_id;
  const { employees } = useAdminLeaves();

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const methods = useForm({
    defaultValues: ADMIN_LEAVE_APPLICATION_DEFAULT_VALUES,
  });
  const { reset, handleSubmit, watch } = methods;
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  useEffect(() => {
    if (!open) return;
    reset(ADMIN_LEAVE_APPLICATION_DEFAULT_VALUES);
  }, [open, reset]);

  useEffect(() => {
    if (!endDate) {
      methods.setValue("endSession", "");
    }
  }, [endDate, methods]);

  const submitLeaveApplication = async (data) => {
    if (!adminId) return;

    setSaving(true);

    try {
      await applyLeaveForEmployee(data, adminId);

      toast.success("Leave application successful");

      setOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(
        logError({
          message: "Failed to apply leave for employee",
          error: err,
          code: "ADMIN_LEAVE_APPLY_FAILED",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const renderEmployeeOption = (opt) => (
    <div className="row row--between flex-1">
      <div className="row gap-1">
        <span className="font-medium">{opt.label}</span>
        <span className="text--secondary text-sm">(ID: {opt.value})</span>
      </div>
      <span className="text--secondary text-sm">
        {capitalizeString(opt.department)}
        {opt.designation && ` (${opt.designation})`}
      </span>
    </div>
  );

  const renderEmployeeValue = (selected) => {
    if (!selected) return "Select employee";

    const employee = employees.find((e) => e.id === selected);
    if (!employee) return "Select employee";

    return (
      <div className="row row--between flex-1">
        <div className="row gap-1">
          <span className="font-medium">{employee.name}</span>
          <span className="text--secondary text-sm">
            (ID: {employee.employeeId})
          </span>
        </div>
        <span className="text--secondary text-sm">
          {capitalizeString(employee.department)}
          {employee.designation && ` (${employee.designation})`}
        </span>
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        className="btn cta-outlined-btn"
        onClick={() => setOpen(true)}
        disabled={saving}
      >
        + Add Leave for an employee
      </button>

      <ModalWrapper
        open={open}
        onClose={() => (saving ? {} : setOpen(false))}
        maxWidth="md"
      >
        <FormProvider {...methods}>
          <form
            className="column gap-1"
            onSubmit={handleSubmit(submitLeaveApplication)}
          >
            <SectionTitle title="Apply for leave on behalf of an employee" />

            <ControlledSelect
              id="employeeId"
              name="employeeId"
              label="Employee*"
              placeholder="Select employee"
              rules={leaveApplicationValidation.employeeId}
              options={(employees ?? []).map((emp) => ({
                value: emp.id,
                label: emp.name,
                department: emp.department,
                designation: emp.designation,
              }))}
              renderOption={renderEmployeeOption}
              renderValue={renderEmployeeValue}
              fullWidth
              reserveErrorSpace
            />

            <ControlledSelect
              id="leaveType"
              name="leaveType"
              label="Leave type*"
              placeholder="Select leave type"
              rules={leaveApplicationValidation.leaveType}
              fullWidth
              options={LEAVE_TYPE_OPTIONS}
              reserveErrorSpace
            />

            <div className="column gap-1">
              <span>Start date*</span>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}
              >
                <ControlledDatePicker
                  name="startDate"
                  id="startDate"
                  disablePast
                  minDate={dayjs()}
                  rules={leaveApplicationValidation.startDate}
                  reserveErrorSpace
                />

                <ControlledSelect
                  id="startSession"
                  name="startSession"
                  placeholder="Select start date session"
                  rules={leaveApplicationValidation.startSession}
                  options={LEAVES_SESSION_OPTIONS}
                  reserveErrorSpace
                />
              </Box>
            </div>

            <div className="column gap-1">
              <span>End date</span>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}
              >
                <ControlledDatePicker
                  name="endDate"
                  id="endDate"
                  disablePast
                  minDate={
                    startDate
                      ? dayjs(startDate).add(1, "day")
                      : dayjs().add(1, "day")
                  }
                  rules={leaveApplicationValidation.endDate(startDate)}
                  reserveErrorSpace
                />
                <ControlledSelect
                  id="endSession"
                  name="endSession"
                  placeholder="Select end date session"
                  rules={leaveApplicationValidation.endSession}
                  options={LEAVES_SESSION_OPTIONS}
                  reserveErrorSpace
                />
              </Box>
            </div>

            <ControlledTextField
              id="reason"
              name="reason"
              label="Reason"
              placeholder="Enter a reason"
              rules={leaveApplicationValidation.reason}
              isMultiline
              rows={3}
              showCharCount
              fullWidth
            />

            <ControlledFileUpload
              name="attachment"
              rules={leaveApplicationValidation.attachment}
              autoUpload={false}
              buttonText="Attach file"
              buttonIcon="ion:attach-outline"
              buttonClass="btn text-btn text-btn--medium underline"
              showPreview
              previewPosition="after"
              previewText="view"
            />

            <div className="row row--end gap-3">
              <button
                type="button"
                className="btn outlined-btn"
                onClick={() => setOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="submit" className="btn cta-btn" disabled={saving}>
                Save
              </button>
            </div>
          </form>
        </FormProvider>
      </ModalWrapper>
    </>
  );
}
